import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type { BandProfile } from '../types';
import { createBandProfile } from '../types';
import { reportSaveError, reportSaveOk, clearSaveErrors } from '../lib/saveStatus';
import {
  apiGetBands,
  apiGetBand,
  apiCreateBand,
  apiUpdateBand,
  apiDeleteBand,
} from '../api';
import { autoPreloadAll } from '../lib/autoPreload';

/* ------------------------------------------------------------------ */
/*  Context shape                                                      */
/* ------------------------------------------------------------------ */
export interface ProfileContextValue {
  profiles: BandProfile[];
  activeProfileId: string | null;
  activeProfileSlug: string | null;
  /**
   * Switch the active band. `null` (or `''`) CLEARS the selection and returns
   * to the multi-band state ("Select a band") — the BandSelector's clear entry
   * (LOT LOGIN-ATLAS-SELECTBAND, 2026-11-04).
   */
  setActiveProfile: (id: string | null) => Promise<void>;
  createProfile: (data?: Partial<BandProfile>) => BandProfile;
  updateProfile: (id: string, data: Partial<BandProfile>) => void;
  deleteProfile: (id: string) => void;
  getProfile: (id: string) => BandProfile | null;
  migrationComplete: boolean;
  loading: boolean;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

/* ------------------------------------------------------------------ */
/*  Local helpers (active profile only — kept in localStorage)         */
/* ------------------------------------------------------------------ */
function loadActiveProfileId(): string | null {
  try {
    return localStorage.getItem('bandz-active-profile');
  } catch {
    return null;
  }
}

function saveActiveProfileId(id: string | null) {
  try {
    if (id) localStorage.setItem('bandz-active-profile', id);
    else localStorage.removeItem('bandz-active-profile');
  } catch {
    // ignore
  }
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */
export function ProfileProvider({ children }: { children: ReactNode }) {
  const [profiles, setProfiles] = useState<BandProfile[]>([]);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(
    () => loadActiveProfileId(),
  );
  const activeProfile = activeProfileId ? profiles.find((p) => p.id === activeProfileId) ?? null : null;
  const activeProfileSlug = activeProfile?.slug || activeProfileId || null;
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  // --- Load profiles from server on mount ---
  useEffect(() => {
    mountedRef.current = true;

    const loadBands = async () => {
      try {
        const data = await apiGetBands();
        if (!mountedRef.current) return;
        const mapped: BandProfile[] = data.map((d) => ({
          id: d.id,
          name: d.name,
          slug: d.slug || '',
          image: d.image || d.logo || d.image_url || '',
          color: d.color || '#e94560',
          country: d.country || '',
          address: d.address || '',
          postal_code: d.postal_code || '',
          city: d.city || '',
          // Departure points (2026-11-02, LOT U2c): rebuilt field by field, so a
          // column omitted here is a column the UI shows EMPTY after a reload
          // even though the server has a value (ghost-field class).
          default_airport: d.default_airport || '',
          default_station: d.default_station || '',
          lat: d.lat ?? null,
          lng: d.lng ?? null,
          lastModified: d.lastModified ?? Date.now(),
          schemaVersion: d.schemaVersion ?? 2,
          my_role: d.my_role,
          finance_access: d.finance_access,
          layout_access: d.layout_access,
          permissions: d.permissions,
          // Without this line the read denies never reach the sidebar: the payload
          // is rebuilt field by field here, so a field omitted is a field LOST
          // (found while wiring the page×level grid, 2026-10-08).
          denied: d.denied,
        }));
        setProfiles(mapped);

        // Automatic offline preload: fire-and-forget fetch of every accessible
        // band's data so the offline cache is warm before the user goes offline
        // (the old manual "Offline preload" button is no longer needed). Never
        // awaited — it cannot slow down login.
        autoPreloadAll(data.map((d) => d.id));

        // Keep the saved active profile from localStorage if the user had
        // explicitly opened a band in a previous session. Otherwise start with
        // NO active band so that no band-scoped data is loaded until the user
        // opens one (click "Open Rider" / picks a band in the selector).
        const saved = loadActiveProfileId();
        if (saved) {
          // keep current (from localStorage / initial state)
        } else {
          setActiveProfileId(null);
        }
      } catch (err) {
        reportSaveError('bands', err);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    (async () => {
      await loadBands();
    })();

    // Re-fetch bands after a successful login (session may not have been
    // established on first mount, causing an initial 401).
    const onAuthLogin = () => {
      // The initial 401 is EXPECTED here (mount happens before the session
      // exists) and must never survive the sign-in as a "⚠️ Not saved" banner:
      // clear whatever was recorded while nobody was signed in, THEN reload.
      // Doing it on the listener covers every sign-in path (LoginPage, 2FA,
      // VerifyEmailPage, AuthContext) in one place. (2026-09-24)
      clearSaveErrors();
      void loadBands();
    };
    window.addEventListener('bandz-auth-login', onAuthLogin);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('bandz-auth-login', onAuthLogin);
    };
  }, []);

  // Persist active profile preference
  useEffect(() => {
    saveActiveProfileId(activeProfileId);
  }, [activeProfileId]);

  // Returns a Promise so callers can await the profile switch before
  // navigating — otherwise a page mounted right after navigate() would still
  // read the OLD active profile (e.g. switching band then clicking a menu
  // link brought you back to the previous band). (2026-08-09)
  const setActiveProfile = useCallback(async (idOrSlug: string | null): Promise<void> => {
    // CLEAR path (LOT LOGIN-ATLAS-SELECTBAND) — `null`/`''` drops the active
    // band so every page falls back to the multi-band surface.
    if (!idOrSlug) {
      setActiveProfileId(null);
      saveActiveProfileId(null);
      await new Promise((res) => setTimeout(res, 0));
      return;
    }
    const profile = profiles.find((p) => p.id === idOrSlug || p.slug === idOrSlug);
    if (profile) {
      setActiveProfileId(profile.id);
      // Write synchronously so a full page reload right after the switch
      // already reads the NEW band from localStorage (2026-08-09).
      saveActiveProfileId(profile.id);
      // Let the context re-render with the new active profile BEFORE any
      // caller navigates, so the mounted page reads the NEW band.
      await new Promise((res) => setTimeout(res, 0));
      return;
    }
    // Not cached yet: try resolve slug (or UUID) from server
    try {
      const band = await apiGetBand(idOrSlug);
      if (!band) return;
      setProfiles((prev) => {
        if (prev.some((p) => p.id === band.id)) return prev;
        return [...prev, { id: band.id, name: band.name, slug: band.slug || '', image: band.image_url || band.logo || '', country: band.country || '', address: band.address || '', postal_code: band.postal_code || '', city: band.city || '', lat: band.lat ?? null, lng: band.lng ?? null, lastModified: Date.now(), schemaVersion: band.schemaVersion ?? 2, my_role: band.my_role, finance_access: band.finance_access, layout_access: band.layout_access, permissions: band.permissions, denied: band.denied }];
      });
      setActiveProfileId(band.id);
      saveActiveProfileId(band.id);
      await new Promise((res) => setTimeout(res, 0));
    } catch (err) {
      reportSaveError('bands', err);
    }
  }, [profiles]);

  // ---- WRITE PATHS INSPECTED FOR THE WRITE GATE (lot A2, 2026-09-25) ----
  // `createProfile` / `updateProfile` / `deleteProfile` are NOT self-service:
  // they hit `/api/bands` (POST/PUT/DELETE), mounted AFTER the server's global
  // read-only guard (server/index.js:572) → a `users.role = 'read-only'`
  // account gets a 403 and the optimistic update leaves a red « Not saved »
  // banner. They are DELIBERATELY LEFT OPEN here, and this is structural, not an
  // oversight:
  //   `App.tsx` nests <AuthProvider> INSIDE <ProfileProvider>
  //   (<ProfileProvider><AuthProvider>…), so this provider sits ABOVE the session
  //   context and `useAuth()` would THROW here (AuthContext:90 — “must be used
  //   within an <AuthProvider>”). No session ⇒ no `users.role` ⇒ the capability
  //   rule cannot be evaluated at this level.
  // The gate must therefore be applied by the CALLERS of these three functions
  // (BandSettingsPage, BandListPage, WelcomeWizardPage — all under `src/pages/`,
  // owned by the parallel pages lot) or the provider order in App.tsx has to
  // change first. Reported as a residual item, not silently dropped.
  const createProfile = useCallback(
    (data?: Partial<BandProfile>) => {
      // Create locally first (optimistic)
      const profile = createBandProfile(data);

      // Fire API call
      apiCreateBand({
        id: profile.id,
        name: profile.name,
        image: profile.image,
        color: profile.color,
        schemaVersion: profile.schemaVersion,
      }).then(() => reportSaveOk('bands')).catch((err) => {
        reportSaveError('bands', err);
      });

      setProfiles((prev) => [...prev, profile]);
      setActiveProfileId(profile.id);
      return profile;
    },
    [],
  );

  const updateProfile = useCallback(
    (id: string, data: Partial<BandProfile>) => {
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === id
            ? { ...p, ...data, lastModified: Date.now() }
            : p,
        ),
      );

      // Fire API call
      apiUpdateBand(id, {
        name: data.name,
        image: data.image,
        color: data.color,
        country: data.country,
        address: data.address,
        postal_code: data.postal_code,
        city: data.city,
        default_airport: data.default_airport,
        default_station: data.default_station,
        lat: data.lat,
        lng: data.lng,
        schemaVersion: data.schemaVersion,
      }).then(() => reportSaveOk('bands')).catch((err) => {
        reportSaveError('bands', err);
      });
    },
    [],
  );

  const deleteProfile = useCallback(
    (id: string) => {
      setProfiles((prev) => {
        const remaining = prev.filter((p) => p.id !== id);
        if (activeProfileId === id) {
          const next = remaining.length > 0 ? remaining[0].id : null;
          setActiveProfileId(next);
        }
        return remaining;
      });

      // Fire API call
      apiDeleteBand(id).then(() => reportSaveOk('bands')).catch((err) => {
        reportSaveError('bands', err);
      });
    },
    [activeProfileId],
  );

  const getProfile = useCallback(
    (id: string) => profiles.find((p) => p.id === id) ?? null,
    [profiles],
  );

  return (
    <ProfileContext.Provider
      value={{
        profiles,
        activeProfileId,
        activeProfileSlug,
        setActiveProfile,
        createProfile,
        updateProfile,
        deleteProfile,
        getProfile,
        migrationComplete: true,
        loading,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */
export function useProfileContext(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) {
    throw new Error(
      'useProfileContext must be used within a <ProfileProvider>',
    );
  }
  return ctx;
}
